import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [require2fa, setRequire2fa] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password, require2fa ? totpCode : undefined);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(res.user.role === "admin" ? "/admin" : (location.state?.from?.pathname || "/portal"));
    } else if (res.requires_2fa) {
      setRequire2fa(true);
      toast.info("Two-factor code required");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="login-page">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-12 gap-16">
        {/* Left — editorial column */}
        <div className="lg:col-span-5 hidden lg:block">
          <div className="overline mb-6">§ Trustee Access</div>
          <h1 className="font-serif font-light text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
            Return to the ledger.
          </h1>
          <p className="mt-8 text-lg text-stone-600 font-light leading-relaxed max-w-md">
            Every filing, signed agreement, and trustee reply is preserved in your private
            console. Sign in to continue.
          </p>
          <div className="mt-12 hairline" />
          <p className="caption text-stone-500 mt-6">MMXXVI · Vol. I · § Correspondence</p>
        </div>

        {/* Right — form column */}
        <div className="lg:col-span-6 lg:col-start-7">
          <div className="border border-stone-200 bg-white p-8 md:p-12">
            <div className="overline mb-4">§ Sign In</div>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight text-stone-900">
              Sign in to the Guild
            </h2>

            <form onSubmit={onSubmit} className="mt-10 space-y-8">
              <div>
                <label htmlFor="email" className="label-luxury">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={require2fa}
                  className="input-luxury"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label htmlFor="password" className="label-luxury">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={require2fa}
                  className="input-luxury"
                  data-testid="login-password-input"
                />
              </div>

              {require2fa && (
                <div className="border border-stone-900 bg-stone-50 p-5" data-testid="login-2fa-block">
                  <label htmlFor="totp" className="label-luxury flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Authenticator code
                  </label>
                  <input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    autoFocus
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    className="input-luxury font-mono tracking-[0.5em] text-center text-xl"
                    data-testid="login-2fa-input"
                  />
                  <p className="caption text-stone-500 mt-3">
                    Open your authenticator app and enter the current code.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={busy || (require2fa && totpCode.length < 6)}
                className="btn-primary w-full justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit-btn"
              >
                {busy ? "Signing in…" : (require2fa ? "Verify & sign in" : "Sign in")}
                <ArrowRight className="h-4 w-4" />
              </button>

              {require2fa && (
                <button
                  type="button"
                  onClick={() => { setRequire2fa(false); setTotpCode(""); }}
                  className="btn-ghost w-full justify-center"
                  data-testid="login-2fa-cancel"
                >
                  Use a different account
                </button>
              )}
            </form>

            <div className="rule my-10">
              <span className="caption">or</span>
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              className="btn-outline w-full justify-center"
              data-testid="login-google-btn"
            >
              Continue with Google
            </button>

            <p className="text-sm text-center mt-10 text-stone-600">
              New to the Guild?{" "}
              <Link to="/register" className="text-stone-900 underline underline-offset-4 hover:text-stone-500">
                Apply for membership
              </Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
